import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import cn from 'classnames';
import { find, isNil, upperCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import EPSwitch from '@/comp/EPSwitch';
import { maxLength, onlyDigits } from '@/utils/util-functions';
import { useMeasurement } from '@/hooks/useMeasurement';
import { ENGINE_VERSION_1_0 } from '@/store/Engine/constants';

import { useStores } from '../../../../../hooks/use-stores';
import EPTable from '../../../../EPTable';
import TableHeaderCell from '../Common/TableHeaderCell/TableHeaderCell';
import { DefaultRuleContainer, Input } from './styled';
import ValidationTooltip from '../Common/ValidationTooltip/ValidationTooltip';

const { useToken } = theme;
export type DefaultSetpointsFrom = 'rulesEditor' | 'rulesGeneratorModal';

export const LONG_DASH = '\u2014';

const StationParametersComponent = observer(
  ({
    isEditMode,
    className,
    from,
    disabledCompressors,
    hasTriedToSubmit,
  }: {
    isEditMode: boolean;
    className?: string;
    from?: DefaultSetpointsFrom;
    disabledCompressors?: string;
    hasTriedToSubmit?: boolean;
  }) => {
    const { engineStore } = useStores();
    const { tunit } = useMeasurement();
    const { t } = useTranslation();
    const fromRulesEditor = from === 'rulesEditor';
    const [, setEditingStationParameterId] = useState('');
    const { token } = useToken();
    const [isExpanded, setIsExpanded] = useState(false);

    const stationParameters = [engineStore.stationConfiguration?.stationParameter];
    const unit = engineStore.unitMesurementSystem;
    const { tvalue } = useMeasurement();

    useEffect(() => {
      if (hasTriedToSubmit) setEditingStationParameterId(null);
    }, [hasTriedToSubmit]);

    useEffect(() => {
      if (engineStore.engineVersion !== ENGINE_VERSION_1_0) {
        engineStore.validateAll();
      }
    }, [engineStore.hasFinishedWizard]);

    const getPopupContainer = trigger => trigger.parentElement;
    const onInputScroll = e => e?.target?.blur();
    const onInputBlur = () => {
      setEditingStationParameterId(null);
    };
    const onDefaultStationParameterKeyPress = event => onlyDigits(event);
    const inputCountArgs = maxLength(4);

    const fieldsIncludingMinutes = [
      'emergencyStartPriorityRotationDelayMinutes',
      'minAllowedUnloadTestingAttemptRateMinutes',
      'minAllowedStartTimeIntervalMinutes',
      'minAllowedRestartTimeIntervalMinutes',
      'minIntervalFromHighFlowTrigger',
      'unloadTestingCommandFrequencySeconds',
      'kpiTimeIntervalMinutes',
    ];

    const onStationParameterFieldChange = (key: string, field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (fieldsIncludingMinutes.includes(field)) {
        const value = e?.target?.value;
        if (value.includes('.')) {
          return;
        }
      }

      const newValue = e?.target?.value;
      setEditingStationParameterId(`${key}-${field}`);
      engineStore.stationConfiguration.setStationParameterField(key, field, newValue);
      engineStore.validateAll(tvalue);
    };

    const onSwitchClicked = (checked: boolean) => {
      setIsExpanded(checked);
    };

    const columns = [
      {
        dataIndex: 'stationTargetPressure',
        key: 'stationTargetPressure',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('targetPressure')} (${tunit('Bar', upperCase(unit))})`} />,

        render: (stationTargetPressure: number, { key }: { key: string }) =>
          !isEditMode && isNil(stationTargetPressure) ? (
            <span className={cn(isEditMode && 'margin-left-2', 'view-mode-table-cell')}>
              {stationTargetPressure ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'stationTargetPressure' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'stationTargetPressure' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={stationTargetPressure}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'stationTargetPressure')}
                onWheel={onInputScroll}
                bordered={false}
                count={maxLength(4)}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $valueLength={`${stationTargetPressure ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'normalPressureOffsetFromTarget',
        key: 'normalPressureOffsetFromTarget',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('offsetFromTargetPressure')}  (${tunit('Bar', upperCase(unit))})`} />,

        render: (normalPressureOffsetFromTarget, { key, compressorId }) =>
          !isEditMode && isNil(normalPressureOffsetFromTarget) ? (
            <span className={cn(isEditMode && 'margin-left-2', fromRulesEditor && 'view-mode-table-cell')}>
              {normalPressureOffsetFromTarget ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'normalPressureOffsetFromTarget' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'normalPressureOffsetFromTarget' })?.tArgs}
              placement="bottom"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={normalPressureOffsetFromTarget}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'normalPressureOffsetFromTarget')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${normalPressureOffsetFromTarget ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'reloadPressure',
        key: 'reloadPressure',
        title: (
          <TableHeaderCell titleKey={`${t('pressureForReloadingCompressor')}  (${tunit('Bar', upperCase(unit))})`} />
        ),
        width: 150,
        render: (reloadPressure, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {reloadPressure ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'reloadPressure' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'reloadPressure' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={reloadPressure}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'reloadPressure')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${reloadPressure ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'startPressure',
        key: 'startPressure',
        width: 150,
        title: (
          <TableHeaderCell titleKey={`${t('pressureForStartingCompressor')} (${tunit('Bar', upperCase(unit))})`} />
        ),
        render: (startPressure, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {startPressure ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'startPressure' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'startPressure' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={startPressure}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'startPressure')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${startPressure ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'emergencyStartPressure',
        key: 'emergencyStartPressure',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('pressureForEmergencyStart')} (${tunit('Bar', upperCase(unit))})`} />,
        render: (emergencyStartPressure, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {emergencyStartPressure ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'emergencyStartPressure' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'emergencyStartPressure' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={emergencyStartPressure}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'emergencyStartPressure')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${emergencyStartPressure ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'minAllowedActiveCompressors',
        key: 'minAllowedActiveCompressors',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('minLoadedCompressors')}`} />,
        render: (minAllowedActiveCompressors, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {minAllowedActiveCompressors ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'minAllowedActiveCompressors' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'minAllowedActiveCompressors' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={minAllowedActiveCompressors}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'minAllowedActiveCompressors')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${minAllowedActiveCompressors ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'unloadingTestPressureFactor',
        key: 'unloadingTestPressureFactor',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('pressureFactor')} (${tunit('Bar', upperCase(unit))})`} />,
        render: (unloadingTestPressureFactor, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {unloadingTestPressureFactor ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'unloadingTestPressureFactor' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'unloadingTestPressureFactor' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={unloadingTestPressureFactor}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'unloadingTestPressureFactor')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${unloadingTestPressureFactor ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'minAllowedUnloadTestingAttemptRateMinutes',
        key: 'minAllowedUnloadTestingAttemptRateMinutes',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('minFrequency')}`} />,
        render: (minAllowedUnloadTestingAttemptRateMinutes, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {minAllowedUnloadTestingAttemptRateMinutes ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                find(engineStore.validationMessages, { field: 'minAllowedUnloadTestingAttemptRateMinutes' })?.message
              }
              tArgs={
                find(engineStore.validationMessages, { field: 'minAllowedUnloadTestingAttemptRateMinutes' })?.tArgs
              }
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={minAllowedUnloadTestingAttemptRateMinutes}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'minAllowedUnloadTestingAttemptRateMinutes')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${minAllowedUnloadTestingAttemptRateMinutes ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'unloadCommandMinTargetSafetyPercentage',
        key: 'unloadCommandMinTargetSafetyPercentage',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('minPressureSafetyMargin')}`} />,
        render: (unloadCommandMinTargetSafetyPercentage, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {unloadCommandMinTargetSafetyPercentage ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                find(engineStore.validationMessages, { field: 'unloadCommandMinTargetSafetyPercentage' })?.message
              }
              tArgs={find(engineStore.validationMessages, { field: 'unloadCommandMinTargetSafetyPercentage' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={unloadCommandMinTargetSafetyPercentage}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'unloadCommandMinTargetSafetyPercentage')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${unloadCommandMinTargetSafetyPercentage ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'minAllowedStartTimeIntervalMinutes',
        key: 'minAllowedStartTimeIntervalMinutes',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('minCompressorStartInterval')}`} />,
        render: (minAllowedStartTimeIntervalMinutes, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {minAllowedStartTimeIntervalMinutes ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'minAllowedStartTimeIntervalMinutes' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'minAllowedStartTimeIntervalMinutes' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={minAllowedStartTimeIntervalMinutes}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'minAllowedStartTimeIntervalMinutes')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${minAllowedStartTimeIntervalMinutes ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },

      {
        dataIndex: 'minAllowedRestartTimeIntervalMinutes',
        key: 'minAllowedRestartTimeIntervalMinutes',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('minCompressorRestartInterval')}`} />,
        render: (minAllowedRestartTimeIntervalMinutes, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {minAllowedRestartTimeIntervalMinutes ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'minAllowedRestartTimeIntervalMinutes' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'minAllowedRestartTimeIntervalMinutes' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={minAllowedRestartTimeIntervalMinutes}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'minAllowedRestartTimeIntervalMinutes')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${minAllowedRestartTimeIntervalMinutes ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'unloadTestingFlowPeakBuffer',
        key: 'unloadTestingFlowPeakBuffer',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('flowPeakBuffer')} (${tunit('m^3/min', upperCase(unit))})`} />,
        render: (unloadTestingFlowPeakBuffer, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {unloadTestingFlowPeakBuffer ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'unloadTestingFlowPeakBuffer' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'unloadTestingFlowPeakBuffer' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={unloadTestingFlowPeakBuffer}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'unloadTestingFlowPeakBuffer')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${unloadTestingFlowPeakBuffer ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'minIntervalFromHighFlowTrigger',
        key: 'minIntervalFromHighFlowTrigger',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('minIntervalHighFlowTrigger')}`} />,
        render: (minIntervalFromHighFlowTrigger, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {minIntervalFromHighFlowTrigger ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'minIntervalFromHighFlowTrigger' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'minIntervalFromHighFlowTrigger' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={minIntervalFromHighFlowTrigger}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'minIntervalFromHighFlowTrigger')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${minIntervalFromHighFlowTrigger ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'unloadTestingCommandFrequencySeconds',
        key: 'unloadTestingCommandFrequencySeconds',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('setpointChangeInterval')}`} />,
        render: (unloadTestingCommandFrequencySeconds, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {unloadTestingCommandFrequencySeconds ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'unloadTestingCommandFrequencySeconds' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'unloadTestingCommandFrequencySeconds' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={unloadTestingCommandFrequencySeconds}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'unloadTestingCommandFrequencySeconds')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${unloadTestingCommandFrequencySeconds ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'kpiTimeIntervalMinutes',
        key: 'kpiTimeIntervalMinutes',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('kpiIntervalRetrieval')}`} />,
        render: (kpiTimeIntervalMinutes, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {kpiTimeIntervalMinutes ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'kpiTimeIntervalMinutes' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'kpiTimeIntervalMinutes' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={kpiTimeIntervalMinutes}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'kpiTimeIntervalMinutes')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${kpiTimeIntervalMinutes ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'loadBalancingPressureFactor',
        key: 'loadBalancingPressureFactor',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('loadBalancingPressureFactor')} (${tunit('Bar', upperCase(unit))})`} />,
        render: (loadBalancingPressureFactor, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {loadBalancingPressureFactor ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'loadBalancingPressureFactor' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'loadBalancingPressureFactor' })?.tArgs}
              placement="bottom"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={loadBalancingPressureFactor}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'loadBalancingPressureFactor')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${loadBalancingPressureFactor ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'emergencyStartPriorityRotationDelayMinutes',
        key: 'emergencyStartPriorityRotationDelayMinutes',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('emergencyStartFrequency')}`} />,
        render: (emergencyStartPriorityRotationDelayMinutes, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {emergencyStartPriorityRotationDelayMinutes ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                find(engineStore.validationMessages, { field: 'emergencyStartPriorityRotationDelayMinutes' })?.message
              }
              tArgs={
                find(engineStore.validationMessages, { field: 'emergencyStartPriorityRotationDelayMinutes' })?.tArgs
              }
              placement="bottom"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={emergencyStartPriorityRotationDelayMinutes}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'emergencyStartPriorityRotationDelayMinutes')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${emergencyStartPriorityRotationDelayMinutes ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'bovAvgUpperBoundThresholdPercentage',
        key: 'bovAvgUpperBoundThresholdPercentage',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('bovMin')}`} />,
        render: (bovAvgUpperBoundThresholdPercentage, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {bovAvgUpperBoundThresholdPercentage ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'bovAvgUpperBoundThresholdPercentage' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'bovAvgUpperBoundThresholdPercentage' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={bovAvgUpperBoundThresholdPercentage}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'bovAvgUpperBoundThresholdPercentage')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${bovAvgUpperBoundThresholdPercentage ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'compressorBovUpperBoundThresholdPercentage',
        key: 'compressorBovUpperBoundThresholdPercentage',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('bovMax')}`} />,
        render: (compressorBovUpperBoundThresholdPercentage, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {compressorBovUpperBoundThresholdPercentage ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                find(engineStore.validationMessages, { field: 'compressorBovUpperBoundThresholdPercentage' })?.message
              }
              tArgs={
                find(engineStore.validationMessages, { field: 'compressorBovUpperBoundThresholdPercentage' })?.tArgs
              }
              placement="bottom"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={compressorBovUpperBoundThresholdPercentage}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'compressorBovUpperBoundThresholdPercentage')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${compressorBovUpperBoundThresholdPercentage ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
      {
        dataIndex: 'maxLoadBuffer',
        key: 'maxLoadBuffer',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('fullLoadPressureBuffer')}`} />,
        render: (maxLoadBuffer, { key, compressorId }) =>
          !isEditMode ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {maxLoadBuffer ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={find(engineStore.validationMessages, { field: 'maxLoadBuffer' })?.message}
              tArgs={find(engineStore.validationMessages, { field: 'maxLoadBuffer' })?.tArgs}
              placement="bottom"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={maxLoadBuffer}
                placeholder="+"
                onChange={onStationParameterFieldChange(key, 'maxLoadBuffer')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultStationParameterKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${maxLoadBuffer ?? ''}`?.length}
                style={{
                  color: engineStore.configurationStatus === 'published' ? token.colorBorder : token.colorPrimary,
                  textDecoration: engineStore.configurationStatus === 'published' ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
        hidden: !isExpanded,
      },
    ];

    const newColumns = columns.filter(({ hidden }) => !hidden);

    return stationParameters?.length && stationParameters[0] ? (
      <DefaultRuleContainer
        className={className}
        $fromRulesEditor={fromRulesEditor}
        $hasFinishedWizard={true}
        $isReadOnly={engineStore.isReadOnly}
      >
        <div className="expand-data-container" style={{ pointerEvents: 'auto' }}>
          <EPSwitch onChange={onSwitchClicked} />
        </div>

        <EPTable
          columns={newColumns}
          dataSource={stationParameters}
          pagination={false}
          className="table"
          scroll={{ x: newColumns.length * 150 }}
        />
      </DefaultRuleContainer>
    ) : null;
  },
);

export default StationParametersComponent;
